/**
 * 탭 통합 스트림 관리자 — SubscribeSessionStream 연결 **하나**의 소유자.
 *
 * 설계 기록(SM docs/simulations-list-streaming-decision.md)의 클라이언트 반쪽:
 * 세션 이벤트·시뮬 상태·태스크 프레임이 봉투 하나로 오고, 이 연결 자체가
 * 세션 liveness다(BFF가 수립 시 SM AttachSession을 대신 쥔다). 브라우저
 * HTTP/1.1의 origin당 6연결 한도 안에서 탭이 상시 1연결만 쓰게 하는 것이
 * 존재 이유(로컬 starvation 실증의 근본 해결).
 *
 * 구독 변경 = 재수립(D1): 원하는 전체 집합+재개 커서로 새 스트림을 열고 옛
 * 것을 끊는다. SM 쪽 공백은 RUNNING 미무장·유휴 10분 유예·epoch 대체가
 * 덮는다(설계서 D1의 검증 근거). 같은 틱의 등록/해제 연쇄는 마이크로태스크
 * 디바운스로 1회 재수립으로 합쳐진다.
 *
 * kind 라우팅: SESSION_EVENT → applySessionEvent(+(세대, seq) 커서), 태스크 kind →
 * 등록별 routeEnvelope(모르는 task_id는 무해 no-op라 다중 등록 팬아웃이
 * 안전하다). 상태 라벨은 제어 응답(P1)과 세션 이벤트만 나른다 — 데이터
 * 평면은 텔레메트리 전용이고, 시뮬 상태 스냅샷 kind 는 계약에서 은퇴했다.
 *
 * 커서는 (세대, seq) 다. 세션 이벤트 로그는 SM 레플리카 프로세스마다 따로라, 재구성·입양 뒤에는 다른 세대의
 * 로그가 seq 1 부터 다시 시작한다 — 옛 세대의 seq 로 재구독하면 새 로그의 종료 통지를 영영 못 받았다(로컬 실측
 * 2026-10-07: 공백 중 완주했는데 배지 RUNNING). 받은 이벤트의 세대가 기억한 것과 다르면 커서를 0 으로 되돌리고
 * 그 이벤트부터 적용하며, 재수립 요청엔 세대를 함께 싣는다(SM 이 다르면 보존분 전부를 다시 준다).
 *
 * 끊긴 뒤의 재수립은 두 단계다: 먼저 재접속 대조(주입된 reconcile — GetSessionState 로 세션의 실행 집합을 받아
 * 스토어를 맞춘다; 이벤트가 못 오는 경우의 마지막 안전망)를 지나야 스트림을 다시 연다. 그동안 세션 스토어의
 * streamLink 가 'reconnecting' 이라 화면이 공백을 상태로 보인다.
 */

import { createPromiseClient } from '@connectrpc/connect';
import { transportMeaningOf, type TransportMeaning } from '@/shared/transportMeaning';
import { createConnectTransport } from '@connectrpc/connect-web';
import { SimTaskStream } from '@/stubs/stream/sim_task_stream_connect';
import {
  SimStreamKind,
  SubscribeSessionStreamRequest,
  type SimStreamEnvelope,
} from '@/stubs/stream/sim_task_stream_pb';
import { SessionEvent } from '@/stubs/sm/simulation_manager_pb';
import { BFF_URL, createAuthInterceptor } from '@/app/services/connectCommon';
import { applySessionEvent, clearPendingTaskEnded } from '@/app/services/sm/sessionEvents';
import { useSessionStore } from '@/app/stores/sessionStore';
import {
  routeEnvelope,
  type FrameDedup,
  type FrameGate,
  type TaskRoute,
} from '@/app/services/sse/simStreamRouter';

/** 태스크 프레임 소비 등록 — startSimStreams 어댑터가 만든다 */
export interface SimStreamRegistration {
  simId: string;
  taskIds: string[];
  routes: Map<string, TaskRoute>;
  dedup?: FrameDedup;
  gate?: FrameGate;
}

const transport = createConnectTransport({
  baseUrl: `${BFF_URL}/api`,
  interceptors: [
    createAuthInterceptor({
      // 순환 의존(simulationManagerService ↔ 이 모듈) 회피 — 세션 id는
      // startSessionStreamMux가 심어 주는 지역 상태에서 읽는다.
      getSessionId: () => activeSessionId,
    }),
  ],
});
const streamClient = createPromiseClient(SimTaskStream, transport);

const RETRYABLE_BACKOFF_BASE_MS = 500;
const RETRYABLE_BACKOFF_MAX_MS = 10_000;

/**
 * 끊김의 갈래 — 실패의 뜻(정본 `transportMeaning`)이 정한다.
 * - retry: 답을 잃었거나·서버가 받지 않았거나·서버 실패 — 백오프로 다시 붙는다.
 * - session-lost: 서버가 세션을 모른다 — 세션 소실(수명주기가 세션을 접고 대조한다).
 * - await-auth: 토큰 만료(인증 서버 장애 중 갱신을 못 했다) — 끝이 아니다. «재접속 중» 으로 두고 인증이 새로워질 때까지
 *   기다렸다가 다시 붙는다(주입된 기다림). 영구로 두면 토큰이 갱신돼도 새로고침 전까지 화면이 굳는다(실측 2026-10-15).
 *   로그아웃이면 수명 신호가 끊겨 루프가 멈춘다.
 * - permanent: 다시 해도 같다(권한·인자·미지원 등) — 재시도가 소음일 뿐이고 소비자 onError 가 영영 안
 *   불리는 무한 루프가 된다(#316 리뷰). 소비자에 전파하고 멈춘다.
 */
type Disconnect = 'retry' | 'session-lost' | 'await-auth' | 'permanent';
const DISCONNECT_BY_MEANING: Readonly<Record<TransportMeaning, Disconnect>> = {
  disconnected: 'retry',
  busy: 'retry',
  fault: 'retry',
  gone: 'session-lost',
  'auth-expired': 'await-auth',
  refused: 'permanent',
};

const classifyDisconnect = (error: unknown): Disconnect => DISCONNECT_BY_MEANING[transportMeaningOf(error)];

/** 영구 오류를 살아 있는 소비자 전원에 전파 — mux는 멈추고 사용자 조치(재로그인 등)를 기다린다 */
function fanOutPermanentError(error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error));
  for (const reg of simRegistrations.values()) {
    for (const route of reg.routes.values()) route.callbacks.onError?.(err);
  }
}

// ── 탭 전역 상태 (연결 1개 = 모듈 싱글턴) ─────────────────────────────
let activeSessionId: string | null = null;
let lifecycleSignal: AbortSignal | null = null;
let onSessionLost: ((error: Error) => void) | null = null;
let reconcileOnReconnect: ReconnectReconcile | null = null;
let awaitAuthRenewal: AuthRenewal | null = null;
let eventsAfterEpoch = 0n;
let eventsAfterSeq = 0n;
// 세대를 배운 뒤에 오는 세대 0 프레임(버전이 섞인 롤아웃의 구 레플리카) 전용 dedup 커서 — 배운 세대의 seq 와 섞지
// 않는다(섞으면 (11, 5) 뒤 (0, 100) 이 커서를 (11, 100) 으로 밀어 세대 11 의 6~100 을 잃는다)
let legacyAfterSeq = 0n;
let generation = 0;
/**
 * 세대를 올린다 — 세대를 올리는 길은 이것 하나다(시작·정지·구독 변경의 재수립). 옛 세대의 인증 기다림은 그 세대와 함께 끝난다:
 * 새 세대는 기다려야 하면 스스로 들고, 옛 세대는 기다림을 끝낼 때 자기 세대일 때만 지운다(그 사이 선 새 세대의 기다림을 지우지 않게).
 * 전에는 시작만 기다림을 비워, 기다리던 중 정지하거나 재수립되면 «인증 서버 응답 대기 중» 이 끝없이 남았다.
 */
function nextGeneration(): number {
  generation += 1;
  useSessionStore.getState().endAuthWait('session-stream');
  return generation;
}
let currentAbort: AbortController | null = null;
let reestablishScheduled = false;

const simRegistrations = new Map<string, SimStreamRegistration>();

function backoffMs(attempt: number): number {
  const base = Math.min(RETRYABLE_BACKOFF_MAX_MS, RETRYABLE_BACKOFF_BASE_MS * 2 ** attempt);
  return base + Math.floor(Math.random() * 250);
}

function waitAbortable(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done, { once: true });
    if (signal.aborted) done();
  });
}

function buildRequest(): SubscribeSessionStreamRequest {
  const sims = new Map<string, string[]>();
  for (const reg of simRegistrations.values()) sims.set(reg.simId, reg.taskIds);
  return new SubscribeSessionStreamRequest({
    eventsAfterEpoch,
    eventsAfterSeq,
    sims: [...sims.entries()].map(([simId, taskIds]) => ({ simId, taskIds })),
  });
}

/**
 * @param current 이 봉투를 나른 스트림이 아직 현재 세대인가. 선수립-후해제 겹침 창의 옛 스트림 프레임도 라우팅은
 *   한다(데이터 프레임은 새 스트림이 재생하지 않는다, 세션 이벤트는 seq dedup 이 거른다) — 다만 **로그 세대 교체는
 *   현재 스트림만** 할 수 있다: 옛 스트림의 늦은 프레임이 옛 세대를 들고 오면 커서를 되돌리게 되므로 버린다.
 */
function routeMuxEnvelope(envelope: SimStreamEnvelope, current: boolean): void {
  switch (envelope.kind) {
    case SimStreamKind.SESSION_EVENT: {
      const event = SessionEvent.fromBinary(envelope.payload);
      // 세대 0 은 «세대를 모르는 구 SM» 이지 세대가 아니다 — 세대를 이미 배운 뒤 0 이 오면(레플리카 버전이
      // 섞인 롤아웃) 배운 세대·seq 는 건드리지 않고 전용 커서로만 거른다. 배운 세대를 지우면 세대 있는 레플리카로
      // 돌아올 때 «또 세대 교체» 로 보여 이미 적용한 이벤트를 다시 재생하고, seq 를 섞으면 그 세대의 이벤트를 잃는다.
      if (event.logEpoch === 0n && eventsAfterEpoch !== 0n) {
        if (event.seq <= legacyAfterSeq) return;
        legacyAfterSeq = event.seq;
        applySessionEvent(event);
        return;
      }
      // 세대가 다르면 로그가 다시 시작한 것(다른 레플리카의 삶) — 커서를 되돌리고 이 이벤트부터 적용한다.
      if (event.logEpoch !== eventsAfterEpoch) {
        if (!current) {
          console.warn(
            `[sessionStreamMux] 옛 스트림의 다른 세대 이벤트(${event.logEpoch}, seq ${event.seq}) — 버림`,
          );
          return;
        }
        console.warn(
          `[sessionStreamMux] 세션 이벤트 로그 세대 교체 ${eventsAfterEpoch}→${event.logEpoch} — 커서 0 부터`,
        );
        eventsAfterEpoch = event.logEpoch;
        eventsAfterSeq = 0n;
        legacyAfterSeq = 0n;
      }
      // seq는 같은 세대 안의 dedup 키 — 커서 이하는 이미 본 이벤트다. 선수립-
      // 후해제 겹침 창에서 옛/새 스트림이 같은 보존 이벤트를 배달하거나 옛
      // 스트림의 늦은 프레임이 커서를 후퇴시키는 것을 여기서 차단한다(#316 리뷰).
      if (event.seq <= eventsAfterSeq) return;
      eventsAfterSeq = event.seq;
      applySessionEvent(event);
      return;
    }
    default:
      // 태스크 kind — 등록별 팬아웃. 봉투의 task_id가 그 등록의 routes에
      // 없으면 routeEnvelope가 무해하게 무시한다.
      for (const reg of simRegistrations.values()) {
        routeEnvelope(envelope, reg.routes, reg.dedup, reg.gate);
      }
  }
}

/** 끊긴 뒤 재수립 직전의 대조 — 세션 lifecycle 이 주입한다(이 모듈은 SM 클라이언트를 모른다). NotFound 는 던져 세션 소실로 */
export type ReconnectReconcile = (sessionId: string, signal: AbortSignal) => Promise<void>;

/** 인증이 새로워질 때까지의 기다림 — 세션 lifecycle 이 주입한다(이 모듈은 인증 백엔드를 모른다). 신호가 끊기면 곧바로 끝난다 */
export type AuthRenewal = (signal: AbortSignal) => Promise<void>;

// 공백(끊김~재수립) 사이에 스트림이 끊긴 채 다시 붙기를 기다리는 중인가 — 구독 변경에 의한 재수립(선수립-후해제)은
// 끊김이 아니라 대조하지 않는다
let lostSinceLastOpen = false;

function setStreamLink(link: 'connected' | 'reconnecting'): void {
  useSessionStore.getState().setStreamLink(link);
}

/** 끊김을 처리한다 — 세션 소실·영구 오류면 'end'(루프 종료), 인증 기다림이면 'await-auth', 재시도면 'retry' */
function handleDisconnect(error: unknown): 'end' | 'await-auth' | 'retry' {
  const kind = classifyDisconnect(error);
  if (kind === 'session-lost') {
    console.warn('[sessionStreamMux] 세션이 서버에 없음 — 세션 소실 처리');
    setStreamLink('connected');
    onSessionLost?.(error as Error);
    return 'end';
  }
  // 기다림을 주입받지 못했으면(수명주기 밖) 인증 만료도 종전처럼 영구로
  if (kind === 'permanent' || (kind === 'await-auth' && !awaitAuthRenewal)) {
    console.error('[sessionStreamMux] 영구 오류 — 재시도 중단, 소비자에 전파', error);
    setStreamLink('connected');
    fanOutPermanentError(error);
    return 'end';
  }
  return kind;
}

async function runMuxLoop(myGeneration: number): Promise<void> {
  const signal = lifecycleSignal;
  const mySessionId = activeSessionId;
  if (!signal || !mySessionId) return;
  let attempt = 0;

  while (!signal.aborted && generation === myGeneration) {
    const abort = new AbortController();
    currentAbort = abort;
    const onLifecycleAbort = () => abort.abort();
    signal.addEventListener('abort', onLifecycleAbort, { once: true });

    try {
      if (lostSinceLastOpen && reconcileOnReconnect) {
        // 끊긴 뒤의 재수립 — 공백 중 일어난 일을 먼저 사실로 맞춘다(세션 응답이 정본). 이 조회가 곧 서버 도달
        // 확인이라, 실패하면 스트림을 열지 않고 같은 백오프로 다시 온다.
        await reconcileOnReconnect(mySessionId, abort.signal);
        if (abort.signal.aborted || generation !== myGeneration) return;
        // 서버에 닿았고 대조를 지났다 — 공백 끝. 스트림 자체는 열어도 첫 프레임(이벤트가 있어야 온다)이나
        // 실패 전까지 아무 신호가 없어(서버는 첫 메시지와 함께 헤더를 보낸다) 여기가 유일한 증거다
        lostSinceLastOpen = false;
        setStreamLink('connected');
      }
      const stream = streamClient.subscribeSessionStream(buildRequest(), {
        signal: abort.signal,
      });
      for await (const envelope of stream) {
        // abort는 이미 settle된 프레임을 버려 주지 않는다 — stop/세션 전환
        // 뒤 지각 도착한 봉투가 다음 세션의 스토어·커서를 오염시키지 않게
        // 소유권을 확인한다. 같은 세션의 세대 겹침(선수립-후해제)은 허용(#316 리뷰).
        if (activeSessionId !== mySessionId) return;
        attempt = 0;
        const current = generation === myGeneration;
        // 대조 없이 다시 연 경우(주입된 대조가 없을 때)의 증거는 첫 프레임이다 — 단 **현재 세대의** 프레임만.
        // 선수립-후해제 겹침 창의 옛 스트림 프레임은 데이터는 나르지만 연결 상태를 말하지 못한다(현재
        // 스트림이 실패해 reconnecting 인데 옛 프레임이 connected 로 되돌리고 다음 대조를 건너뛰게 한다).
        if (current) {
          lostSinceLastOpen = false;
          setStreamLink('connected');
        }
        routeMuxEnvelope(envelope, current);
      }
      // 구독 변경으로 끊긴 옛 세대의 스트림이 예외 대신 정상 종료로 끝나기도 한다 — 그것은 단절이 아니다
      // (새 세대가 이미 섰다). 오류 경로와 같은 소유권 확인.
      if (abort.signal.aborted || generation !== myGeneration) return;
      // 서버 정상 종료 = 제어 소스 단절(SM 세션 정리·failover 등) — 재수립으로
      // 확인한다: 세션이 진짜 죽었으면 다음 라운드의 attach가 NOT_FOUND를 낸다.
      console.warn('[sessionStreamMux] 스트림 종료 — 재수립');
      lostSinceLastOpen = true;
      setStreamLink('reconnecting');
    } catch (error) {
      if (abort.signal.aborted || generation !== myGeneration) return;
      const next = handleDisconnect(error);
      if (next === 'end') return;
      lostSinceLastOpen = true;
      setStreamLink('reconnecting');
      if (next === 'await-auth' && awaitAuthRenewal) {
        console.warn('[sessionStreamMux] 인증 만료 — 인증이 새로워지면 다시 붙는다', error);
        // 기다리는 동안 화면이 그 사실을 보인다(«인증 서버 응답 대기 중») — 숨기지 않는다
        useSessionStore.getState().beginAuthWait('session-stream');
        try {
          await awaitAuthRenewal(signal);
        } finally {
          // 그 사이 새 세대가 섰으면 기다림은 새 세대의 것이다 — 옛 세대가 지우지 않는다(새 세대는 설 때 비우고 스스로 든다)
          if (generation === myGeneration) useSessionStore.getState().endAuthWait('session-stream');
        }
        attempt = 0;
        continue;
      }
      console.warn('[sessionStreamMux] 스트림 오류 — 재수립 예정', error);
      attempt += 1;
    } finally {
      signal.removeEventListener('abort', onLifecycleAbort);
    }
    if (signal.aborted || generation !== myGeneration) return;
    await waitAbortable(backoffMs(attempt), signal);
  }
}

/**
 * 구독 집합 변경 → 재수립. 선수립-후해제: 새 세대 루프를 먼저 시작하고 옛
 * 연결을 끊는다(새 요청이 곧 전체 진실 + 커서 재개라 프레임 계약은 유지).
 * 같은 틱의 연쇄 변경은 1회로 합친다.
 */
function scheduleReestablish(): void {
  if (!activeSessionId || !lifecycleSignal || lifecycleSignal.aborted) return;
  if (reestablishScheduled) return;
  reestablishScheduled = true;
  queueMicrotask(() => {
    reestablishScheduled = false;
    if (!activeSessionId || !lifecycleSignal || lifecycleSignal.aborted) return;
    const previousAbort = currentAbort;
    void runMuxLoop(nextGeneration());
    previousAbort?.abort();
  });
}

/**
 * 세션 lifecycle이 세션 id 확보 직후 호출 — 이때부터 이 연결이 liveness다.
 * onSessionLost: 서버가 세션을 모른다(NOT_FOUND)고 확정됐을 때 1회 — 재시도
 * 없음(세션 재발급은 lifecycle 소관).
 * reconcile: 끊긴 뒤 재수립 직전의 재접속 대조(없으면 대조 없이 다시 연다).
 * authRenewal: 토큰 만료로 거절됐을 때 인증이 새로워질 때까지의 기다림(없으면 인증 만료도 영구 오류).
 */
export function startSessionStreamMux(
  sessionId: string,
  signal: AbortSignal,
  onLost: (error: Error) => void,
  reconcile: ReconnectReconcile | null = null,
  authRenewal: AuthRenewal | null = null,
): void {
  activeSessionId = sessionId;
  lifecycleSignal = signal;
  onSessionLost = onLost;
  reconcileOnReconnect = reconcile;
  awaitAuthRenewal = authRenewal;
  lostSinceLastOpen = false;
  setStreamLink('connected');
  // 커서가 (0, 0) 으로 돌아가 이 세션의 보존 통지가 다시 온다 — 옛 세션의 보류분은 여기 닿지 않는다
  eventsAfterEpoch = 0n;
  eventsAfterSeq = 0n;
  legacyAfterSeq = 0n;
  clearPendingTaskEnded();
  const previousAbort = currentAbort;
  void runMuxLoop(nextGeneration());
  previousAbort?.abort();
}

/** 로그아웃/세션 종료 — 연결과 등록 전부 정리 */
export function stopSessionStreamMux(): void {
  nextGeneration();
  currentAbort?.abort();
  currentAbort = null;
  activeSessionId = null;
  lifecycleSignal = null;
  onSessionLost = null;
  reconcileOnReconnect = null;
  awaitAuthRenewal = null;
  lostSinceLastOpen = false;
  setStreamLink('connected');
  eventsAfterEpoch = 0n;
  eventsAfterSeq = 0n;
  legacyAfterSeq = 0n;
  clearPendingTaskEnded();
  simRegistrations.clear();
}

/** 태스크 프레임 구독 등록 — 해제 함수를 돌려준다 */
export function registerSimStreams(registration: SimStreamRegistration): () => void {
  simRegistrations.set(registration.simId, registration);
  scheduleReestablish();
  return () => {
    if (simRegistrations.get(registration.simId) === registration) {
      simRegistrations.delete(registration.simId);
      scheduleReestablish();
    }
  };
}

/** 테스트·진단용 — 현재 구독 스냅샷 */
export function muxDebugState(): {
  sessionId: string | null;
  sims: string[];
  eventsAfterEpoch: bigint;
  eventsAfterSeq: bigint;
} {
  return {
    sessionId: activeSessionId,
    sims: [...simRegistrations.keys()],
    eventsAfterEpoch,
    eventsAfterSeq,
  };
}
